import { Add as AddIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';

export interface BankItem { id: string; name: string; subtitle?: string }

const BankCloneModal: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  items: BankItem[];
  onConfirm: (ids: string[]) => void;
  loading: boolean;
}> = ({ open, onClose, title, items, onConfirm, loading }) => {
  const { lang } = useDirection();
  const [selected, setSelected] = React.useState<Set<string>>(new Set());

  React.useEffect(() => { if (open) setSelected(new Set()); }, [open]);

  const toggle = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {tr('bank.noItems', lang)}
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {items.map(item => (
              <FormControlLabel
                key={item.id}
                control={<Checkbox checked={selected.has(item.id)} onChange={() => toggle(item.id)} size="small" />}
                label={
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{item.name}</Typography>
                    {item.subtitle && (
                      <Typography sx={{ fontSize: '0.775rem', color: '#64748B' }}>{item.subtitle}</Typography>
                    )}
                  </Box>
                }
                sx={{ alignItems: 'flex-start', py: 0.5 }}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>{tr('common.cancel', lang)}</Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(Array.from(selected))}
          disabled={selected.size === 0 || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {tr('bank.addSelected', lang)} ({selected.size})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BankCloneModal;
